import { fetchFleetRucLicenses, getRucStatusForFleet } from "@/api/vtnz-backend/rucLicenses";
import type { FetchState } from "@/types/sharedTypes";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { RucLicense, RucStatus } from "./types";

/**
 * Parses an ISO date string into a millisecond timestamp, treating an
 * unparseable string as infinitely old rather than producing `NaN` - a
 * `NaN` in a sort comparator doesn't throw, but it does make the sort order
 * undefined, which would let a row with a corrupt `purchased_at` land
 * anywhere (including "newest") instead of predictably last.
 */
function safeTimestamp(dateString: string): number {
  const ms = new Date(dateString).getTime();
  return Number.isFinite(ms) ? ms : -Infinity;
}

/** True for a finite, non-negative number - a $/km or $/1000km rate can legitimately be 0, but never negative or NaN/Infinity. */
function isValidRate(value: number): boolean {
  return Number.isFinite(value) && value >= 0;
}

/**
 * Resolves a single RUC license row's rate per 1000km, in NZD.
 *
 * `rate_per_1000km` is nullable on this row even though it's presumably
 * derived from `amount_paid` and the distance range - possibly a PostgREST
 * quirk where it's computed at write time rather than always populated. When
 * it's missing, this recomputes it from the row's other (non-nullable)
 * fields instead of giving up: `amount_paid / (distance_end_km -
 * distance_start_km) * 1000`. Returns `undefined` when neither the raw nor
 * the computed value is usable (e.g. a zero/negative distance range, which
 * would otherwise divide by zero) - this is reached with real API data that
 * may not satisfy the generated types' non-null guarantees at runtime, so
 * every field is re-checked rather than trusted.
 */
function rateForLicense(license: RucLicense): number | undefined {
  if (
    typeof license.rate_per_1000km === "number" &&
    isValidRate(license.rate_per_1000km)
  ) {
    return license.rate_per_1000km;
  }

  const distanceCovered = license.distance_end_km - license.distance_start_km;

  if (!Number.isFinite(distanceCovered) || distanceCovered <= 0) {
    return undefined;
  }

  if (
    typeof license.amount_paid !== "number" ||
    !Number.isFinite(license.amount_paid) ||
    license.amount_paid < 0
  ) {
    return undefined;
  }

  const computedRate = (license.amount_paid / distanceCovered) * 1000;

  return isValidRate(computedRate) ? computedRate : undefined;
}

export const useRucStore = defineStore("rucStore", () => {
  // ---------------------------------- State --------------------------------- //

  /** reactive array to hold the fleet's RUC license rows fetched from the API */
  const licenses = ref<RucLicense[]>([]);

  /** indicates the licenses fetch's current state */
  const licensesFetchState = ref<FetchState>("idle");
  const licensesFetchErrorMsg = ref<string | null>(null);

  /**
   * reactive array to hold the fleet's RUC status per vehicle, fetched from
   * the `ruc_status_for_fleet` RPC (already compares each vehicle's latest
   * odometer reading against its current license server-side).
   */
  const statuses = ref<RucStatus[]>([]);

  /** indicates the statuses fetch's current state */
  const statusesFetchState = ref<FetchState>("idle");
  const statusesFetchErrorMsg = ref<string | null>(null);

  // --------------------------------- Getters -------------------------------- //

  /**
   * helper flag to indicate any of this store's fetches are in operation.
   * Good for loading animations. Each source tracks its own `*FetchState`
   * separately since `fetchRuc` runs them concurrently and a single shared
   * flag would race.
   */
  const isFetching = computed(
    () =>
      licensesFetchState.value === "loading" ||
      statusesFetchState.value === "loading",
  );

  /**
   * Returns the RUC license history belonging to the given vehicle.
   *
   * `vehicleExternalId` is the vehicle's *external* id (`vehicle_id` on the
   * `ruc_licenses` row, matching `Vehicle.externalId`) - not a local/domain
   * id.
   */
  function getLicensesForVehicle(vehicleExternalId: string): RucLicense[] {
    return licenses.value.filter(
      (license) => license.vehicle_id === vehicleExternalId,
    );
  }

  /**
   * Returns the current RUC status for the given vehicle, or `undefined` if
   * the vehicle has no RUC license.
   */
  function getStatusForVehicle(
    vehicleExternalId: string,
  ): RucStatus | undefined {
    return statuses.value.find(
      (status) => status.vehicle_id === vehicleExternalId,
    );
  }

  /**
   * Returns the vehicle's most recently-purchased usable RUC rate, in NZD
   * per 1000km, or `undefined` if it has no license with a usable rate.
   *
   * "Most recent" is by `purchased_at` (the real purchase date), not
   * `created_at`, matching the purchase history ordering elsewhere. Walks
   * the vehicle's licenses newest-first and returns the first one
   * `rateForLicense` can resolve a value for (raw or computed) - a vehicle's
   * newest license row might itself have neither a usable raw rate nor a
   * computable one (e.g. corrupt distance range), so this doesn't assume
   * the newest row is necessarily the usable one.
   */
  function getLatestRateForVehicle(
    vehicleExternalId: string,
  ): number | undefined {
    const sortedLicenses = [...getLicensesForVehicle(vehicleExternalId)].sort(
      (a, b) => safeTimestamp(b.purchased_at) - safeTimestamp(a.purchased_at),
    );

    for (const license of sortedLicenses) {
      const rate = rateForLicense(license);
      if (rate !== undefined) return rate;
    }

    return undefined;
  }

  // --------------------------------- Actions -------------------------------- //

  /** fetches the fleet's RUC license rows from the backend and assigns them to state. */
  async function fetchLicenses() {
    licensesFetchState.value = "loading";
    licensesFetchErrorMsg.value = null;

    const fetchRes = await fetchFleetRucLicenses();

    if (!fetchRes.success) {
      licensesFetchErrorMsg.value = fetchRes.errorMsg;
      licensesFetchState.value = "error";
      return;
    }

    licenses.value = fetchRes.data;
    licensesFetchState.value = "success";
  }

  /** fetches the fleet's per-vehicle RUC status from the backend and assigns it to state. */
  async function fetchStatuses() {
    statusesFetchState.value = "loading";
    statusesFetchErrorMsg.value = null;

    const fetchRes = await getRucStatusForFleet();

    if (!fetchRes.success) {
      statusesFetchErrorMsg.value = fetchRes.errorMsg;
      statusesFetchState.value = "error";
      return;
    }

    statuses.value = fetchRes.data;
    statusesFetchState.value = "success";
  }

  /**
   * Orchestration entry point: fetches every RUC source this store covers.
   * This is the single action `appStore`'s bootstrap orchestration should
   * call.
   */
  async function fetchRuc() {
    await Promise.all([fetchLicenses(), fetchStatuses()]);
  }

  function $reset() {
    licensesFetchState.value = "idle";
    licenses.value = [];

    statusesFetchState.value = "idle";
    statuses.value = [];
  }

  return {
    // --------------------------------- State -------------------------------- //

    /** reactive array to hold the fleet's RUC license rows fetched from the API */
    licenses,

    /** indicates the licenses fetch's current state */
    licensesFetchState,

    /** error message for the last licenses fetch error, if there is one. */
    licensesFetchErrorMsg,

    /** reactive array to hold the fleet's per-vehicle RUC status fetched from the API */
    statuses,

    /** indicates the statuses fetch's current state */
    statusesFetchState,

    /** error message for the last statuses fetch error, if there is one. */
    statusesFetchErrorMsg,

    // -------------------------------- Getters ------------------------------- //

    /** helper flag to indicate any of this store's fetches are in operation. Good for loading animations. */
    isFetching,

    /** Returns the RUC license history belonging to the given vehicle. */
    getLicensesForVehicle,

    /** Returns the current RUC status for the given vehicle, or `undefined` if it has none. */
    getStatusForVehicle,

    /** Returns the vehicle's most recently-purchased usable RUC rate (NZD/1000km), or `undefined` if it has none. */
    getLatestRateForVehicle,

    // -------------------------------- Actions ------------------------------- //

    /** fetches the fleet's RUC license rows from the backend and assigns them to state. */
    fetchLicenses,

    /** fetches the fleet's per-vehicle RUC status from the backend and assigns it to state. */
    fetchStatuses,

    /** Orchestration entry point: fetches every source this store covers. */
    fetchRuc,

    /** Resets the store to its initial state. */
    $reset,
  };
});
